import Parse from "parse";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { markParcelAsDisabled, setParcelsForStagesInStore } from "../../actions/parcels";
import { updateToastInfo } from "../../actions/settings";
import { updateSpinnerState } from "../../actions/spinner";
import ParcelStatusPage from "../../components/common/parcelStatusPages";
import { getParcelsByStatusAndSubStatus, updateParcelDataWithInfo } from "../../parse-functions/parcels";
import { Routes } from "../../router/routes";
import { convertArrayToObj } from "../../utils/json";
import { buybackReceivedTableColumns } from "./buybackUtils";
import { setFiltersInStore } from "../../actions/filters";
import { returnEndOfDay, returnStartOfDay } from "../../utils/datetime";
import BuybackReceivedModal from "./BuyBackReceivedModal";


const BuyBackReceivedList = () => {
    const pageStatus = "buybackpicked";
    const { t } = useTranslation();
    const breadCrumbItems = [{
        title: t("BuyBack"),
        link: Routes.BuyBack.Pending.path
    }, {
        title: t("BuyBack Received"),
        link: Routes.BuyBack.Received.path
    }];
    const dispatch = useDispatch();

    const [selectedParcelId, setSelectedParcelId] = useState(null);
    const [showBuybackReceivedModal, setShowBuybackReceivedModal] = useState(false);
    const storeCode = useSelector((state) => state?.user?.warehouse?.warehouseCode || "");
    const filtersValue = useSelector((state) => state.filters?.[pageStatus]);
    const parcels = useSelector((state) => state?.parcels?.[pageStatus] || {});
    const zone = useSelector((state) => state?.user?.warehouse?.zone || "");
    
    const handleFiltersSubmit = (startDate, endDate) => {
        dispatch(setFiltersInStore({
            pageType: pageStatus,
            data: {
              startDate: new Date(startDate),
              endDate: new Date(endDate)
            }
          }))
        getAvailableParcels(startDate, endDate);
    };

    const getAvailableParcels = async (startDate, endDate) => {
        dispatch(updateSpinnerState(true));
        try {
            const filters = {
                subStatus: [pageStatus, "buybackpickedcashcollected"],
                storeCode,
                startDate: new Date(startDate),
                endDate: new Date(endDate),
                zone,
                missingKeys: ["wingsTransferNo"]
            };
            const parcels = await getParcelsByStatusAndSubStatus(filters);
            dispatch(setParcelsForStagesInStore(pageStatus, convertArrayToObj(JSON.parse(JSON.stringify(parcels)), "objectId")))
            dispatch(updateSpinnerState(false));
            dispatch(updateToastInfo({
                show: true,
                type: 'success',
                title: t('Orders fetched'),
                message: t("You can mark all your buybacks as received from here")
            }))

        } catch (e) {
            dispatch(updateSpinnerState(false));
            dispatch(updateToastInfo({
                show: true,
                type: 'danger',
                title: t('Error'),
                message: t(e.message)
            }))
        }
    }
    const onRowSelect = (value, objectId) => {
        if (selectedParcelId) {
            dispatch(updateToastInfo({
                show: true,
                type: 'danger',
                title: t('Only 1 item allowed'),
                message: t("Please deselect other item before choosing this")
            }))
        } else {
            if (value) {
                setSelectedParcelId(objectId);
            } else {
                setSelectedParcelId(null);
            }
        }
    }
    const onRefreshClick = () => {
        const currentStartDate = filtersValue.startDate ? new Date(filtersValue.startDate): returnStartOfDay(new Date());
        const currentEndDate = filtersValue.endDate ? new Date(filtersValue.endDate) : returnEndOfDay(new Date());
        getAvailableParcels(currentStartDate, currentEndDate)
    }
    
    const onRescheduleConfirm = async (transferNo) => {
        dispatch(updateSpinnerState(true));
        try {
            const formData = {
                data: {
                    buybackReceivedAt: new Date(),
                    buybackReceivedBy: Parse.User.current().getUsername(),
                    wingsTransferNo: transferNo,
                    buybackReceivedWarehouse: storeCode
                },
                objectId: selectedParcelId
            }
            await updateParcelDataWithInfo(formData);
            dispatch(markParcelAsDisabled(pageStatus, selectedParcelId));
            dispatch(updateToastInfo({
                show: true,
                type: 'success',
                title: t('Buyback Received'),
                message: t("This parcel's buyback has been received")
            }))
            setSelectedParcelId(null);
            dispatch(updateSpinnerState(false));

        } catch(e) {
            dispatch(updateSpinnerState(false));
        }
    };
    const handleBuyBackClose = (transferNo) => {
        onRescheduleConfirm(transferNo);
        setShowBuybackReceivedModal(false);
    }
    const handleItemBulkUpdate = (actionId) => {
        if (selectedParcelId) {
            switch (actionId) {
                case "receive": {
                    // open modal to mark the buyback as closed.
                    setShowBuybackReceivedModal(true);
                    break;
                }
                default: {
                    dispatch(updateToastInfo({
                        show: true,
                        type: 'danger',
                        title: t('Select an Action'),
                        message: t("Please select an action from the dropdown")
                    }))
                    break;
                }
            }
        } else {
            dispatch(updateToastInfo({
                show: true,
                type: 'danger',
                title: t('Select Item'),
                message: t("Please select an item to take action")
            }))
        }
    }
    return (
        <>
            <ParcelStatusPage
                pageType={pageStatus}
                filtersValue={filtersValue}
                filterSubmit={handleFiltersSubmit}
                tableData={parcels}
                tableColumns={buybackReceivedTableColumns(onRowSelect, storeCode)}
                handleRefresh={onRefreshClick}
                pageTitle={t("Buyback Received")}
                breadCrumbItems={breadCrumbItems}
                pageSubTitle={t("All the parcels whose buyback has been received can be viewed here")}
                searchPlaceHolder={t("Search by Invoice No. in Database")}
                handleItemBulkUpdate={handleItemBulkUpdate}
                bulkActionOptions={[{
                    label: "Receive",
                    value: "receive"
                }]}
                enableBulkActions={selectedParcelId && Object.keys(parcels)?.length > 0}
            />
            <BuybackReceivedModal
                showModal={showBuybackReceivedModal}
                closeModal={() => {
                    setShowBuybackReceivedModal(false);
                }}
                onSubmit={handleBuyBackClose}
                />
        </>
    )
}
export default BuyBackReceivedList;